import React, { ReactNode, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { get, isEmpty } from 'lodash'
import { useParams } from 'react-router-dom'
import { LoadingTypes } from '../../modules/loading/reducer'
import { isLoading } from '../../utils/loadingState'
import { ProductType } from '../../types'
import { useOnboardingAccountId } from '../../utils/hooks/useCrmOnboardingContext'
import { useUserType } from '../../utils/hooks/useUserType'
import type { CloudSettingsState } from './cloudSettingsState'
import {
  awsTemplateNameCMCM,
  fetchAccounts,
  getAccountRegistrationData,
} from './cloudSettingsAccountActions'
import { useSellerInfo } from './useSellerInfo'

export type AwsCloudSettingsForm = React.FC<{
  cloudType: string
  productType: ProductType
  productAccounts: any
}>

interface AwsCloudSettingsProps {
  children: ReactNode | AwsCloudSettingsForm
}

const AwsCloudSettings = ({ children }: AwsCloudSettingsProps) => {
  const productType = useSelector(
    (state: CloudSettingsState) =>
      (state.platformSettings.selectedProductOnSettings as ProductType) ||
      'oppsync'
  )
  const dispatch = useDispatch() as (action: unknown) => Promise<unknown>
  const { accountId: accountIdFromParams } = useParams<{ accountId: string }>()
  const accountIdFromHook = useOnboardingAccountId()
  const partnerType = useUserType()

  const accountId = useMemo(() => {
    return accountIdFromParams || accountIdFromHook || ''
  }, [accountIdFromParams, accountIdFromHook])

  const lastFetchedAccountIdRef = useRef<string | null>(null)

  const accountRegistrationData = useSelector((state: CloudSettingsState) => {
    return {
      accounts: state.accounts[productType],
      rows: state.accountForm[productType]?.accountDetails?.AWS.roles.rows,
    }
  })

  const AWSData = useSelector(
    (state: CloudSettingsState) =>
      state.accountForm[productType].accountDetails.AWS
  )
  const isTemplateEmpty = isEmpty(AWSData.roles.rows[0])

  const lastProductAccount = get(
    accountRegistrationData,
    'accounts[0]',
    undefined
  )

  const { loading: sellerInfoLoading, flyoutSellerInfo } = useSellerInfo()

  const isLoadingAccounts = useSelector((state: CloudSettingsState) =>
    isLoading(state.loading)(LoadingTypes.CMCM_FETCHING_ACCOUNTS)
  )
  const isLoadingTemplates = useSelector((state: CloudSettingsState) =>
    isLoading(state.loading)(LoadingTypes.TEMPLATES)
  )
  const loading = isLoadingAccounts || isLoadingTemplates || sellerInfoLoading

  useEffect(() => {
    if (accountId && !isLoadingAccounts) {
      const isDifferentAccount = lastFetchedAccountIdRef.current !== accountId
      if (isDifferentAccount) {
        lastFetchedAccountIdRef.current = accountId
        dispatch(fetchAccounts(awsTemplateNameCMCM, 'AWS', productType))
      }
    }
  }, [accountId, productType, dispatch, isLoadingAccounts])

  useEffect(() => {
    if (!accountId || isLoadingAccounts || isLoadingTemplates) {
      return
    }

    const isDifferentAccount = lastFetchedAccountIdRef.current !== accountId

    const shouldHaveOrigin =
      productType === 'oppsync' ||
      (productType === 'flyout' && !isEmpty(flyoutSellerInfo.origin))
    if (shouldHaveOrigin && (isTemplateEmpty || isDifferentAccount)) {
      dispatch(
        getAccountRegistrationData(
          'AWS',
          awsTemplateNameCMCM,
          productType,
          accountId,
          productType,
          partnerType,
          lastProductAccount,
          flyoutSellerInfo
        )
      )
    }
  }, [
    accountId,
    lastProductAccount,
    productType,
    isTemplateEmpty,
    flyoutSellerInfo,
    isLoadingAccounts,
    isLoadingTemplates,
    partnerType,
    dispatch,
  ])

  return !loading ? (
    <>
      {typeof children === 'function'
        ? children({
            cloudType: 'AWS',
            productType,
            productAccounts: accountRegistrationData.accounts,
          })
        : children}
    </>
  ) : (
    <></>
  )
}

export default AwsCloudSettings
